import FullWidthLayout from "@/layouts/FullWidthLayout";
import KTripsTable from "kinnect-front/KYard/Trips/KTripsTable";
import { KAlertPanel, KDatePicker, KTextField } from "kinnect-general";
import { Broadcastable, FetchesData, Pageable } from "kinnect-mixins";
import {
	VCard,
	VCol,
	VExpandTransition,
	VRow,
	VSheet
} from "vuetify/lib";
import { YardTrip } from "kinnect-models";
import { debounce, removeByObjectValue } from "kinnect-helpers";
import { formatDate } from "kinnect-formatters";
import { isSameDay, parseISO } from "date-fns";

export default {
	name: "YardTripsPage",

	metaInfo: { title: "Yard - Trips" },

	mixins: [
		Broadcastable,
		FetchesData,
		Pageable
	],

	data: () => ( {
		datePickerDialog: false,
		searchTrips: [],
		trips: [],
		tripsAlerts: [],
		search: "",
		today: ( new Date() ).setHours( 0, 0, 0, 0 ),
		types: {},
		broadcasts: {
			Model: YardTrip,
			channel: "trip",
			propKey: "trips",
			eventNames: {
				c: "trip.created",
				u: "trip.updated",
				d: "trip.deleted"
			}
		},
		tripQuerySelect: {
			address: [ "id", "address", "alias", "city", "phone", "state", "zip", "lat", "lng" ],
			customer: [ "id", "name" ],
			drivers: [ "id", "idUser" ],
			truck: [ "id", "number", "type" ],
			user: [ "id", "firstName", "lastName" ]
		},
		tripQueryInclude: [
			"drivers.user",
			"errors",
			"schedules",
			"schedules.address",
			"schedules.customer",
			"schedules.salesman.user",
			"schedules.schedule-types",
			"truck"
		]
	} ),

	computed: {
		date: {
			get() {
				return this.$route.query.date;
			},

			set( date ) {
				this.$router.push( { query: { date } } );
			}
		},

		selectedDate() {
			return this.date || formatDate( this.today, "yyyy-MM-dd" );
		},

		displayDate() {
			return formatDate( this.selectedDate );
		},

		selectedDateTrips() {
			return this.trips.filter( trip => isSameDay( parseISO( this.selectedDate ), parseISO( trip.deliveryDate ) ) );
		},

		displayedTrips() {
			return this.search ? this.searchTrips : this.selectedDateTrips;
		},

		displayedScheduledTrips() {
			return this.displayedTrips.filter( trip => !trip.completed );
		},

		displayedCompletedTrips() {
			return this.displayedTrips.filter( trip => trip.completed );
		},

		numScheduledTrips() {
			return this.displayedScheduledTrips.length;
		},

		numCompletedTrips() {
			return this.displayedCompletedTrips.length;
		},

		breadcrumbDetails() {
			return `${this.numScheduledTrips} scheduled, ${this.numCompletedTrips} completed trips`;
		},

		toolbarActions() {
			if ( this.$can( "add_yard_trips" ) ) {
				return {
					icon: "add_circle",
					text: "Add Trip",
					to: { name: "yardTripAdd" },
					tooltip: "Add a new trip"
				};
			}
		}
	},

	watch: {
		selectedDate() {
			this.fetchTrips();
		},

		breadcrumbDetails( val ) {
			this.$store.dispatch( "breadcrumbs/setDetails", val );
		},

		search() {
			this.fetchSearchResults();
		}
	},

	methods: {
		updateInArrById( arr, updatedItem ) {
			const updateIndex = arr.findIndex( trip => updatedItem.id === trip.id );

			if ( updateIndex !== -1 ) {
				this.$set( arr, updateIndex, updatedItem );
			}
		},

		onTripRemoved( trip ) {
			this.trips = removeByObjectValue( this.trips, "id", trip.id );
			this.fetchTripsAlerts();
		},

		onTripAlertRemoved( trip ) {
			this.tripsAlerts = removeByObjectValue( this.tripsAlerts, "id", trip.id );
			this.fetchTripsAlerts();
		},

		onTripUpdated( updatedTrip ) {
			this.updateInArrById( this.displayedTrips, updatedTrip );
		},

		onTripAlertUpdated( updatedTrip ) {
			this.updateInArrById( this.tripsAlerts, updatedTrip );
		},

		fetchSearchResults: debounce( async function() {
			if ( this.search ) {
				const queryWhenCallStarted = this.search;
				const trips = await YardTrip
				.custom( "search/trips" )
				.params( { query: queryWhenCallStarted } )
				.get();

				if ( this.search === queryWhenCallStarted ) {
					this.searchTrips = trips;
				}
			} else {
				this.searchTrips = [];
			}
		}, 500 ),

		async fetchTrips() {
			const dateWhenCallStarted = this.selectedDate;
			const trips = await YardTrip
			.where( "deliveryDate", dateWhenCallStarted )
			.select( this.tripQuerySelect )
			.include( this.tripQueryInclude )
			.get();

			if ( this.selectedDate === dateWhenCallStarted ) {
				this.trips = trips;
			}
		},

		async fetchTripsAlerts() {
			if ( this.$can( "edit_yard_trips" ) ) {
				this.tripsAlerts = await YardTrip
				.custom( "yard/trips/alerts" )
				.select( this.tripQuerySelect )
				.include( this.tripQueryInclude )
				.get();
			}
		},

		async fetch() {
			await this.$http.all( [
				this.fetchTrips(),
				this.fetchTripsAlerts()
			] );
		}
	},

	render() {
		return <FullWidthLayout>
			<VExpandTransition>
				{Boolean( this.tripsAlerts.length ) && <KAlertPanel>
					<template slot="header">
						{this.tripsAlerts.length === 1
							? "1 scheduled trip has not been marked as completed"
							: `${this.tripsAlerts.length} scheduled trips have not been marked as completed`
						}
					</template>

					<KTripsTable
						hiddenCols={[
							"cost",
							"errors",
							"schedules.length"
						]}
						items={this.tripsAlerts}
						onTripCompleted={this.fetchTripsAlerts}
						onTripRemoved={this.onTripAlertRemoved}
						onTripUpdated={this.onTripAlertUpdated}
					></KTripsTable>
				</KAlertPanel>}
			</VExpandTransition>

			<VCard class="mt-4">
				<VSheet class="px-3" color="primary" tile>
					<VRow>
						<VCol cols={12} sm={4}>
							<KDatePicker
								showFieldArrows
								textFieldProps={{
									dark: true,
									flat: true,
									hideDetails: true,
									label: "Date*",
									prependIcon: null,
									prependInnerIcon: "event",
									soloInverted: true
								}}
								vModel={this.date}
							></KDatePicker>
						</VCol>

						<VCol cols={12} sm={8}>
							<KTextField
								clearable
								dark
								flat
								hideDetails
								label="Search"
								loading={this.$wait.is( "get trips" )}
								prependInnerIcon="search"
								soloInverted
								vModel={this.search}
							></KTextField>
						</VCol>
					</VRow>
				</VSheet>

				<KTripsTable
					hiddenCols={[
						"cost",
						"deliveryDate",
						"errors",
						"schedules.length"
					]}
					items={this.displayedScheduledTrips}
					loading={this.$wait.is( "get trips" )}
					onTripCompleted={this.fetchTripsAlerts}
					onTripRemoved={this.onTripRemoved}
					onTripUpdated={this.onTripUpdated}
					schedulesTableProps={{
						hiddenCols: [
							"actions",
							"age",
							"deliveryDate",
							"deliveryTime",
							"estLoadTime"
						]
					}}
				>
					<template slot="title">
						{this.search
							? <span>Scheduled trips for the search "{this.search}"</span>
							: <span>Scheduled trips for {this.displayDate}</span>
						}
					</template>
				</KTripsTable>

				<KTripsTable
					hiddenCols={[
						"$.status",
						"deliveryDate",
						"estDepartureTime",
						"estReturnTime",
						"schedules.length"
					]}
					items={this.displayedCompletedTrips}
					loading={this.$wait.is( "get trips" )}
				>
					<template slot="title">
						{this.search
							? <span>Completed trips for the search "{this.search}"</span>
							: <span>Completed trips for {this.displayDate}</span>
						}
					</template>
				</KTripsTable>
			</VCard>
		</FullWidthLayout>;
	}
};
